import { useEffect, useState } from "react"

import {
  getTrackingFilterOptions,
  getTrackingStats,
  getTrackingSummary,
  type Loaded
} from "../api/tracking.services.ts"
import type {
  TrackingFilterOptions,
  TrackingQuery,
  TrackingSummary,
  TrackingTile
} from "../types/tracking.types.ts"

export interface Loadable<T> {
  status: "loading" | "loaded" | "error"
  data: T | null
}

function useLoaded<T>(load: () => Promise<Loaded<T>>, key: string): Loadable<T> {
  const [state, setState] = useState<Loadable<T>>({ status: "loading", data: null })
  useEffect(() => {
    let cancelled = false
    setState((previous) => ({ status: "loading", data: previous.data }))
    void load().then((result) => {
      if (cancelled) return
      setState(result.ok && result.data ? { status: "loaded", data: result.data } : { status: "error", data: null })
    })
    return () => {
      cancelled = true
    }
    // `key` stands for whatever `load` closes over.
  }, [key])
  return state
}

/** The tiles take no filters: they are the baseline the table is read against. */
export function useTrackingStats(): Loadable<TrackingTile[]> {
  const stats = useLoaded(getTrackingStats, "stats")
  return { status: stats.status, data: stats.data?.tiles ?? null }
}

/** The strip follows the table's filters (R15.3e); the order is irrelevant to it. */
export function useTrackingSummary(query: TrackingQuery): Loadable<TrackingSummary> {
  const { ordering: _ordering, ...filters } = query
  return useLoaded(() => getTrackingSummary(filters), JSON.stringify(filters))
}

export function useTrackingFilterOptions(): Loadable<TrackingFilterOptions> {
  return useLoaded(getTrackingFilterOptions, "options")
}
